import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrainTypeQuickTest from "@/components/BrainTypeQuickTest";
import OnboardingConclusion from "@/components/OnboardingConclusion";
import { Sparkles, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "perfect", labelKey: "onboarding.category.perfect", hintKey: "onboarding.category.perfect.hint" },
  { key: "very_good", labelKey: "onboarding.category.very_good", hintKey: "onboarding.category.very_good.hint" },
  { key: "good", labelKey: "onboarding.category.good", hintKey: "onboarding.category.good.hint" },
  { key: "okayish", labelKey: "onboarding.category.okayish", hintKey: "onboarding.category.okayish.hint" },
  { key: "bad", labelKey: "onboarding.category.bad", hintKey: "onboarding.category.bad.hint" },
  { key: "very_bad", labelKey: "onboarding.category.very_bad", hintKey: "onboarding.category.very_bad.hint" },
];

const QUESTIONS = [
  { key: "emotions", titleKey: "onboarding.question.emotions.title", descriptionKey: "onboarding.question.emotions.description" },
  { key: "past", titleKey: "onboarding.question.past.title", descriptionKey: "onboarding.question.past.description" },
  { key: "future", titleKey: "onboarding.question.future.title", descriptionKey: "onboarding.question.future.description" },
  { key: "inner_self_talk", titleKey: "onboarding.question.inner_self_talk.title", descriptionKey: "onboarding.question.inner_self_talk.description" },
];

const emptyScores = () => ({
  perfect: "",
  very_good: "",
  good: "",
  okayish: "",
  bad: "",
  very_bad: "",
});

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0..3 are questions, 4 is review
  const [answers, setAnswers] = useState({
    emotions: emptyScores(),
    past: emptyScores(),
    future: emptyScores(),
    inner_self_talk: emptyScores(),
  });
  const [submitting, setSubmitting] = useState(false);
  // Flow ordering:
  //   Prospects WITHOUT a coach-set brain_type: brain_type_test → reflection → conclusion
  //   Prospects WITH a coach-set brain_type   : reflection → conclusion (quick test skipped)
  //   Trainees / Coachees                     : reflection → conclusion
  // Phases: "brain_type_test" | "reflection" | "conclusion".
  const [phase, setPhase] = useState(
    user?.client_type === "prospect" && user?.brain_type == null
      ? "brain_type_test"
      : "reflection"
  );

  const current = QUESTIONS[step];
  const isReview = step === QUESTIONS.length;

  const totals = useMemo(() => {
    const result = {};
    for (const q of QUESTIONS) {
      result[q.key] = CATEGORIES.reduce(
        (sum, c) => sum + (parseInt(answers[q.key][c.key], 10) || 0),
        0
      );
    }
    return result;
  }, [answers]);

  const currentTotal = isReview ? 0 : totals[current.key];
  const remaining = 1000 - currentTotal;

  const setVal = (qkey, ckey, value) => {
    if (value === "") {
      setAnswers((a) => ({ ...a, [qkey]: { ...a[qkey], [ckey]: "" } }));
      return;
    }
    const n = Math.max(0, Math.min(1000, parseInt(value, 10) || 0));
    setAnswers((a) => ({ ...a, [qkey]: { ...a[qkey], [ckey]: String(n) } }));
  };

  const canProceed = !isReview && currentTotal === 1000;
  const allValid = QUESTIONS.every((q) => totals[q.key] === 1000);

  const submit = async () => {
    if (!allValid) {
      toast.error(t("onboarding.toast.must_total"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {};
      for (const q of QUESTIONS) {
        payload[q.key] = {};
        for (const c of CATEGORIES) {
          payload[q.key][c.key] = parseInt(answers[q.key][c.key], 10) || 0;
        }
      }
      await api.post("/onboarding", payload);
      await refresh();
      toast.success(t("onboarding.toast.thanks"));
      // After the 1000-day reflection everyone proceeds directly to the
      // conclusion (the brain-type quick test, if any, has already happened
      // for prospects before the reflection).
      setPhase("conclusion");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const finishConclusion = (route) => {
    // Last screen: navigate to the post-onboarding destination chosen on
    // the conclusion (dashboard for coaching/either bands, events for training).
    navigate(route || "/me/dashboard");
  };

  const finishBrainTypeTest = () => {
    // Quick-test done → move on to the 1000-day reflection (4 questions
    // + review) and then the conclusion.
    setPhase("reflection");
    setStep(0);
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="onboarding-page">
      <header className="border-b border-[#E5E2DC] bg-[#F9F8F6]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
            <div className="leading-tight">
              <span className="font-heading text-base tracking-tight font-semibold text-[#1A1D1A] block">
                NeuroSophia
              </span>
              <span className="text-[10px] italic text-[#5C635A]" data-testid="brand-tagline">
                {t("brand.tagline")}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="light" />
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A9188] font-bold hidden sm:block">
              {t("onboarding.welcome", { name: user?.name?.split(" ")[0] || "" })}
            </p>
          </div>
        </div>
        <div className="h-1 bg-[#E5E2DC]">
          <div
            className="h-full bg-[#4A5D4E] transition-all duration-500"
            style={{
              width: (() => {
                if (phase === "conclusion") return "100%";
                if (phase === "brain_type_test") return "15%";
                // Reflection: 5 screens (4 questions + review). Prospects
                // who took the BT test have already done ≈15%, so their
                // reflection range is 25 → 90%. Everyone else spans 5 → 90%.
                const tookBtTest =
                  user?.client_type === "prospect" && user?.brain_type == null;
                const reflectionStart = tookBtTest ? 25 : 5;
                const reflectionEnd = 90;
                const progress = (step + 1) / (QUESTIONS.length + 1);
                return `${reflectionStart + (reflectionEnd - reflectionStart) * progress}%`;
              })(),
            }}
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {phase === "conclusion" ? (
          <OnboardingConclusion
            answers={answers}
            clientType={user?.client_type}
            onFinish={finishConclusion}
          />
        ) : phase === "brain_type_test" ? (
          <BrainTypeQuickTest onComplete={finishBrainTypeTest} />
        ) : !isReview ? (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-4">
              {t("onboarding.reflection_step", { step: step + 1, total: QUESTIONS.length })}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D1A] font-light leading-[1.1]">
              {t("onboarding.question_prefix")}{" "}
              <span className="font-semibold">{t(current.titleKey)}</span>?
            </h1>
            <p className="mt-4 text-[#5C635A] leading-relaxed max-w-2xl">
              {t(current.descriptionKey)} {t("onboarding.question.distribute")}{" "}
              <span className="font-semibold text-[#1A1D1A]">
                {t("onboarding.question.thousand_days")}
              </span>{" "}
              {t("onboarding.question.among")}
            </p>

            <div
              className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              data-testid={`onboarding-q-${current.key}-grid`}
            >
              {CATEGORIES.map((c) => (
                <div
                  key={c.key}
                  className="bg-white border border-[#E5E2DC] rounded-xl p-5 hover:border-[#4A5D4E]/50 transition-colors"
                >
                  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">
                    {t(c.labelKey)}
                  </Label>
                  <p className="text-[11px] text-[#8A9188] mt-1 mb-3">{t(c.hintKey)}</p>
                  <Input
                    type="number"
                    min={0}
                    max={1000}
                    inputMode="numeric"
                    placeholder="0"
                    value={answers[current.key][c.key]}
                    onChange={(e) => setVal(current.key, c.key, e.target.value)}
                    data-testid={`onboarding-${current.key}-${c.key}-input`}
                    className="h-12 rounded-lg bg-[#F9F8F6] border-[#E5E2DC] text-2xl font-heading font-light text-[#1A1D1A] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
                  />
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-between rounded-xl border border-[#E5E2DC] bg-white px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                  {t("onboarding.days_allocated")}
                </p>
                <p
                  className="font-heading text-3xl font-light text-[#1A1D1A] mt-1"
                  data-testid={`onboarding-${current.key}-total`}
                >
                  {currentTotal}
                  <span className="text-[#8A9188] text-xl"> / 1000</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                  {t("onboarding.remaining")}
                </p>
                <p
                  className={`font-heading text-3xl font-light mt-1 ${
                    remaining === 0
                      ? "text-[#4A5D4E]"
                      : remaining < 0
                      ? "text-[#9C6A53]"
                      : "text-[#B37A5F]"
                  }`}
                >
                  {remaining}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-4">
              {t("onboarding.final_step")}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D1A] font-light leading-[1.1]">
              {t("onboarding.review.title_a")}{" "}
              <span className="font-semibold">{t("onboarding.review.title_b")}</span>
            </h1>
            <p className="mt-4 text-[#5C635A] leading-relaxed">
              {t("onboarding.review.body")}
            </p>

            <div className="mt-10 space-y-3" data-testid="onboarding-review">
              {QUESTIONS.map((q) => (
                <div
                  key={q.key}
                  className="bg-white border border-[#E5E2DC] rounded-xl px-6 py-5 flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-[#1A1D1A] capitalize">{t(q.titleKey)}</p>
                    <p className="text-xs text-[#8A9188] mt-1">
                      {CATEGORIES.map((c) => `${t(c.labelKey)}: ${answers[q.key][c.key] || 0}`).join(" · ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className={`font-heading text-2xl font-light ${
                        totals[q.key] === 1000 ? "text-[#4A5D4E]" : "text-[#9C6A53]"
                      }`}
                    >
                      {totals[q.key]}
                      <span className="text-[#8A9188] text-base"> /1000</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-12 flex items-center justify-between">
          {phase === "brain_type_test" || phase === "conclusion" ? null : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="onboarding-back-button"
                className="h-11 rounded-lg border-[#E5E2DC] bg-transparent text-[#1A1D1A] hover:bg-[#F2F0EA]"
              >
                <ArrowLeft strokeWidth={1.5} size={18} className="mr-2" />
                {t("onboarding.back")}
              </Button>

              {!isReview ? (
                <Button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canProceed}
                  data-testid="onboarding-next-button"
                  className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
                >
                  {step === QUESTIONS.length - 1 ? t("onboarding.review") : t("onboarding.continue")}
                  <ArrowRight strokeWidth={1.5} size={18} className="ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={!allValid || submitting}
                  data-testid="onboarding-submit-button"
                  className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
                >
                  <Check strokeWidth={1.5} size={18} className="mr-2" />
                  {submitting ? t("onboarding.submitting") : t("onboarding.submit")}
                </Button>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
